<template>
  <div class="user-login-form__form-wrapper">
    <form class="user-login-form__form" @submit.prevent="validateFields">
      <div
        v-for="(field, key) in fields"
        v-show="field.type"
        :key="`field-${key}`"
        class="user-login-form__form-group"
      >
        <input
          v-if="field.type && !field.hidden"
          :type="field.type"
          :class="[
            'ine-input user-login-form__form-control',
            errors[key] && 'user-login-form__form-control--error',
          ]"
          @focus="clearErrors"
          :placeholder="field.placeholder"
          :autocomplete="field.autocomplete"
          v-model="field.value"
        />
      </div>

      <div class="user-login-form__actions">
        <div class="user-login-form__actions__group">
          <a class="btn btn--tertiary" :href="forgotPassLink" target="_blank">
            Forgot your password?
          </a>
          <button
            v-if="!isLoadingComponent"
            class="btn btn--primary user-login-form__btn"
            type="submit"
          >
            Sign In
          </button>
          <vue-loaders v-else name="ball-clip-rotate" color="#ef7d22"></vue-loaders>
        </div>
        <div class="user-login-form__oauth">
          <span class="user-login-form__oauth--separator">OR</span>
        </div>
        <button
          v-for="provider in authProviders"
          :key="`provider-${provider.id}`"
          class="btn btn--secondary btn--default-icon-color"
          @click.prevent="loginThroughProvider(provider)"
          :disabled="isLoadingCaptcha"
        >
          <GoogleIcon class="user-login-form__oauth__icon" /> Log In with
          {{ provider.name }}
        </button>
      </div>
    </form>
    <CaptchaBlock
      ref="invisibleRecaptcha"
      keyType="login"
      @captcha-verify-action="submitLoginForm"
    />
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import { validateForm, clearFormErrors } from '@/helpers'
import GoogleIcon from '@/assets/icons/google.svg'
import CaptchaBlock from '@/components/shared/CaptchaBlock.vue'

export default {
  name: 'UserLoginForm',
  components: {
    GoogleIcon,
    CaptchaBlock,
  },
  data() {
    return {
      email: '',
      password: '',
      errors: clearFormErrors(),
      fields: {
        email: {
          type: 'email',
          placeholder: 'Email',
          rules: [],
          value: '',
          autocomplete: 'email',
          hidden: false,
        },
        password: {
          type: 'password',
          placeholder: 'Password',
          rules: ['presence'],
          value: '',
          autocomplete: 'current-password',
          hidden: false,
        },
      },
      forgotPassLink: `//${process.env.VUE_APP_STREAMING_URL}/forgot-password`,
    }
  },
  props: {
    redirectUrl: {
      type: String,
      required: false,
      default: '/',
    },
  },
  methods: {
    ...mapMutations({
      setIsMFAFormShown: 'app/IS_MFA_FORM_SHOWN_UPDATED',
    }),
    fetchLoginRecaptcha({ email, password }) {
      this.email = email
      this.password = password
      this.$refs.invisibleRecaptcha.execute()
    },
    loginThroughProvider(provider) {
      this.$store.dispatch('app/setIsComponentLoading', true)
      const { url } = provider
      this.$store.dispatch('auth/saveRedirectUrl', window.location.href)
      window.open(url, '_parent')
    },
    validateFields() {
      const { fields } = this
      const { isValid, errors } = validateForm(fields)
      this.errors = errors
      if (isValid) {
        const payload = {
          email: fields.email.value,
          password: fields.password.value,
        }
        this.fetchLoginRecaptcha(payload)
      }
    },
    clearErrors() {
      this.errors = clearFormErrors()
    },
    async submitLoginForm({ recaptcha }) {
      const { email, password } = this
      try {
        this.$store.dispatch('app/setIsComponentLoading', true)
        const payload = { email, password, recaptcha }
        await this.$store.dispatch('auth/login', payload)
        if (this.$store.state.auth.isTwoFactorRequired) {
          this.setIsMFAFormShown(true)
        }
        if (this.$store.state.auth.isAuthenticated)
          await this.$store.dispatch('auth/handleLoginSuccess', this.redirectUrl)
        this.$store.dispatch('app/setIsComponentLoading', false)
      } catch (error) {
        if (error?.message) {
          this.$store.dispatch('app/setIsComponentLoading', false)
          return this.$store.dispatch('app/setError', error.message)
        }
        this.$store.dispatch(
          'app/setError',
          'Something went wrong with the login. Please try again.'
        )
      } finally {
        this.$store.dispatch('app/setIsComponentLoading', false)
      }
      this.$store.dispatch('app/setIsComponentLoading', false)
    },
  },
  computed: {
    ...mapState({
      authProviders: state => state.auth.authProviders,
      userProfile: state => state.auth.userProfile,
      isComponentLoading: state => state.app.isComponentLoading,
      isLoadingCaptcha: state => state.app.isLoadingCaptcha,
    }),
    isLoadingComponent() {
      return this.isComponentLoading || this.isLoadingCaptcha
    },
  },
  mounted() {
    this.fields.email.rules.push('presence')
  },
}
</script>
<style lang="scss" scoped>
.user-login-form {
  &__form-control {
    width: 100%;
    margin-bottom: 10px;
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus,
    &:-webkit-autofill:active {
      background: var(--light-blue-gray);
    }
  }
  &__oauth {
    &__icon {
      fill: none !important;
    }
  }
  &__oauth {
    position: relative;
    width: 100%;
    margin: 90px 0 50px 0;
    padding: 20px 0 0 0;
    border-top: 1px solid var(--tundora-gray);
    &--separator {
      font-family: var(--font-bold);
      position: absolute;
      color: var(--white);
      top: -19px;
      width: 38px;
      height: 38px;
      left: 0;
      right: 0;
      margin: 0 auto;
      border-radius: 60px;
      background: var(--dark-brighter);
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  &__actions {
    text-align: center;
    &__group {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 20px;
    }
    &__submit-wrapper {
      margin-top: 0 !important;
    }
  }
  &__with-email {
    color: var(--white);
    font-size: rem(16px);
    margin-bottom: 10px;
  }
  ::v-deep .ine-form__title {
    color: var(--white);
  }
  ::v-deep .ine-form__tagline {
    color: var(--light-blue-gray);
    margin-bottom: 50px;
  }
  ::v-deep .ine-form__submit-btn-wrapper {
    margin: 20px 0 0 auto;
  }
}
</style>
